import { DatabaseSchedulePullOrUpdateOverrideConfigTypeValue } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseSchedulePullOrUpdateOverrideDataSetConfig } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import React from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { DataSetSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSelector';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';

export const DatabaseSchedulePullOrUploadDataSetOverrideConfig = ({
  organizationId,
  databaseId,
  configs,
  setConfigs,
}: {
  organizationId: string;
  databaseId: string;
  configs: DatabaseSchedulePullOrUpdateOverrideDataSetConfig[];
  setConfigs: (newValue: DatabaseSchedulePullOrUpdateOverrideDataSetConfig[]) => void;
}) => {
  const language = useLanguage();

  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  const configTypeList: ListItem<DatabaseSchedulePullOrUpdateOverrideConfigTypeValue>[] =
    useMemo(() => {
      return [
        {
          optionLabel: 'Pull New Version',
          optionValue: 'pull-latest',
        },
        {
          optionLabel: 'Disable Pull',
          optionValue: 'disable-pull',
        },
        {
          optionLabel: 'Take Latest',
          optionValue: 'take-latest',
        },
      ];
    }, []);

    const crudOps = useCrudOperations({
      items: configs,
      setItems: setConfigs,
    });

  return (
    <Spacing direction={'vertical'} size={'md'}>
      <S2Typography
        variant={'headline-5'}
        translatedContent={language.dangerousUserInput({
          value: 'Override Configurations',
        })}
      />
      <S2Typography
        variant={'body-6'}
        translatedContent={language.dangerousUserInput({
          value:
            'Optionally override the default configuration for each data set to either pull it on every sync, or disable pulling it. Default behavior is to pull the latest data set.',
        })}
      />
      <Button
        behavior={{
          type: 'onClick',
          onClick: () => {
            crudOps.onAdd({
              dataSetId: '',
              configType: {
                type: 'pull-latest'
              }
            })
          },
        }}
        text={'Add Config'}
      />
      <Table
        columnDefinitions={[
          {
            id: 'dataSetId',
            header: () => 'Data Set ID',
            cell: ({ item, rowIndex }) => {
              const dataSets =
                listDataSetsPattern.status === 'done'
                  ? listDataSetsPattern.entity.dataSets
                  : [];
              return (
                <div>
                  <DataSetSelector
                    dataSetId={item.dataSetId}
                    setDataSetId={(id) => {
                      if (!id) {
                        return;
                      }
                      crudOps.onEdit(rowIndex, {
                        ...item,
                        dataSetId: id,
                      });
                    }}
                    dataSets={dataSets}
                    label={'Data Set'}
                  />
                </div>
              );
            },
          },
          {
            id: 'configType',
            header: () => 'Config Type',
            cell: ({ item, rowIndex }) => {
              return (
                <div>
                  <SelectBoxWithSearch
                    type={'single'}
                    label={'Config'}
                    selected={item.configType.type}
                    setSelected={(value) => {
                      if (typeof value !== 'string' || !value) {
                        return;
                      }
                      crudOps.onEdit(rowIndex, {
                        ...item,
                        configType: {
                          type: value,
                        },
                      });
                    }}
                    list={configTypeList}
                  />
                </div>
              );
            },
          },
          {
            id: 'delete',
            header: () => 'Delete',
            cell: ({ rowIndex }) => {
              return (
                <Button
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      crudOps.onDelete(rowIndex);
                    },
                  }}
                >
                  Delete
                </Button>
              );
            },
          },
        ]}
        items={configs}
      />
    </Spacing>
  );
};
